import React, { useState, useEffect, useCallback } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import { Select } from '@mantine/core';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from '@/utils/i18n';
import { useAllEventsQuery } from '../graphql/queries/allEvents.generated';

interface EventFilterProps {
  /** The URL parameter name to use for this filter (e.g., 'event', 'eventId') */
  paramName?: string;
  /** Initial value if no URL parameter is present */
  initialValue?: string | null;
  /** Custom label text instead of default "Event" */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Callback when event selection changes */
  onChange?: (value: string | null) => void;
  /** Whether to show the "All" option */
  showAll?: boolean;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether to use Mantine Select (true) or Reactstrap Input (false) */
  useMantineSelect?: boolean;
  /** Whether the select is searchable (only for Mantine) */
  searchable?: boolean;
  /** Whether the select is clearable (only for Mantine) */
  clearable?: boolean;
  /** Whether the component is read-only */
  readOnly?: boolean;
}

/**
 * EventFilter component that automatically persists event selection to URL parameters.
 * This provides consistent event filtering behavior across the admin interface.
 *
 * Features:
 * - Automatic URL parameter persistence
 * - Back button support (filter state survives navigation)
 * - Consistent UI with reactstrap or Mantine styling
 * - Automatic event loading from GraphQL
 * - TypeScript support
 */
export const EventFilter: React.FC<EventFilterProps> = ({
  paramName = 'event',
  initialValue = null,
  label,
  className = '',
  onChange,
  showAll = true,
  disabled = false,
  useMantineSelect = false,
  searchable = true,
  clearable = true,
  readOnly = false,
}) => {
  const history = useHistory();
  const location = useLocation();

  // Initialize filter from URL params
  const urlParams = new URLSearchParams(location.search);
  const eventParam = urlParams.get(paramName) || initialValue;
  const [selectedEvent, setSelectedEvent] = useState<string | null>(eventParam);

  // Load events
  const { loading: loadingEvents, data: eventsData } = useAllEventsQuery({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  // Memoized function to update URL
  const updateUrl = useCallback(
    (eventValue: string | null) => {
      const params = new URLSearchParams(location.search);
      if (eventValue && eventValue !== 'all') {
        params.set(paramName, eventValue);
      } else {
        params.delete(paramName);
      }
      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Only update if the URL would actually change
      if (newUrl !== `${location.pathname}${location.search}`) {
        history.replace(newUrl);
      }
    },
    [history, location.pathname, location.search, paramName],
  );

  // Update URL when selection changes
  useEffect(() => {
    updateUrl(selectedEvent);
    // Call onChange callback if provided
    if (onChange) {
      onChange(selectedEvent === 'all' ? null : selectedEvent);
    }
  }, [selectedEvent, updateUrl, onChange]);

  // Sync state with URL params when URL changes (for back navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const eventParam = urlParams.get(paramName) || initialValue;
    if (eventParam !== selectedEvent) {
      setSelectedEvent(eventParam);
    }
  }, [location.search, paramName, initialValue]);

  // Prepare event options
  const sortedEvents =
    eventsData?.events
      ?.sort((a, b) => {
        if (!a || !a.name || !b || !b.name) {
          return 0;
        }
        return a?.name.localeCompare(b?.name);
      })
      ?.map(({ id, name }) => ({
        value: id,
        label: `${name} (${id})`,
      })) || [];

  if (showAll) {
    sortedEvents.unshift({ value: 'all', label: i18n.t('Common:all') });
  }

  // Handle event selection change
  const handleEventChange = (value: string | null) => {
    if (!readOnly && !disabled) {
      setSelectedEvent(value);
    }
  };

  if (useMantineSelect) {
    return (
      <Select
        className={`form-control ${className}`}
        disabled={loadingEvents || disabled || readOnly}
        value={selectedEvent}
        onChange={handleEventChange}
        data={sortedEvents}
        searchable={searchable && !readOnly}
        clearable={clearable && !readOnly}
        placeholder={label || i18n.t('Common:event')}
      />
    );
  }

  return (
    <InputGroup className={className}>
      <InputGroupText>{label || i18n.t('Common:event')}</InputGroupText>
      <Input
        type="select"
        value={selectedEvent || 'all'}
        onChange={(e) => {
          const value = e.currentTarget.value;
          handleEventChange(value === 'all' ? null : value);
        }}
        disabled={loadingEvents || disabled || readOnly}
      >
        {showAll && <option value="all">{i18n.t('Common:all')}</option>}
        {eventsData?.events?.map(({ id, name }) => (
          <option key={id} value={id}>
            {name} ({id})
          </option>
        ))}
      </Input>
    </InputGroup>
  );
};

export default EventFilter;
